import type {LoggerDomain, LogLevel} from "@cm/log"

const GLOBAL_KEY = "cm:log:level"
const DOMAIN_PREFIX = "cm:log:level:"
const DEFAULT_LEVEL: LogLevel = "error"

/**
 * Manages log level configuration stored in localStorage.
 * Provides methods to get/set global and domain-specific log levels.
 *
 * Log levels are cached in memory to avoid localStorage reads on every log call.
 * The cache is automatically invalidated when levels are changed via setter methods.
 */
export class LoggerConfig {
    private cachedGlobalLevel: LogLevel | null = null
    private readonly cachedDomainLevels = new Map<LoggerDomain, LogLevel | null>()
    private readonly listeners = new Set<() => void>()

    /**
     * Get the global log level. Defaults to "error" if not set.
     * Uses cached value if available.
     */
    getGlobalLevel(): LogLevel {
        if (this.cachedGlobalLevel !== null) {
            return this.cachedGlobalLevel
        }
        this.cachedGlobalLevel = this.readGlobalLevelFromStorage()
        return this.cachedGlobalLevel
    }

    private readGlobalLevelFromStorage(): LogLevel {
        try {
            const stored = localStorage.getItem(GLOBAL_KEY)
            if (stored && isValidLogLevel(stored)) {
                return stored
            }
        } catch {
            // localStorage may not be available in some contexts
        }
        return DEFAULT_LEVEL
    }

    /**
     * Set the global log level.
     */
    setGlobalLevel(level: LogLevel): void {
        this.cachedGlobalLevel = level
        try {
            localStorage.setItem(GLOBAL_KEY, level)
        } catch {
            // localStorage may not be available (e.g., Web Workers): in-memory cache still applies
        }
        this.notify()
    }

    /**
     * Get the log level for a specific domain, or null if not set.
     * Uses cached value if available.
     */
    getDomainLevel(domain: LoggerDomain): LogLevel | null {
        if (this.cachedDomainLevels.has(domain)) {
            return this.cachedDomainLevels.get(domain) ?? null
        }
        const level = this.readDomainLevelFromStorage(domain)
        this.cachedDomainLevels.set(domain, level)
        return level
    }

    private readDomainLevelFromStorage(domain: LoggerDomain): LogLevel | null {
        try {
            const stored = localStorage.getItem(DOMAIN_PREFIX + domain)
            if (stored && isValidLogLevel(stored)) {
                return stored
            }
        } catch {
            // localStorage may not be available
        }
        return null
    }

    /**
     * Set the log level for a specific domain.
     */
    setDomainLevel(domain: LoggerDomain, level: LogLevel): void {
        this.cachedDomainLevels.set(domain, level)
        try {
            localStorage.setItem(DOMAIN_PREFIX + domain, level)
        } catch {
            // localStorage may not be available
        }
        this.notify()
    }

    /**
     * Clear the log level for a specific domain (reverts to global level).
     */
    clearDomainLevel(domain: LoggerDomain): void {
        this.cachedDomainLevels.set(domain, null)
        try {
            localStorage.removeItem(DOMAIN_PREFIX + domain)
        } catch {
            // localStorage may not be available
        }
        this.notify()
    }

    /**
     * Get the effective log level for a domain.
     * Returns domain-specific level if set, otherwise global level.
     */
    getEffectiveLevel(domain: LoggerDomain): LogLevel {
        return this.getDomainLevel(domain) ?? this.getGlobalLevel()
    }

    /**
     * Get all configured domain levels.
     */
    getAllDomainLevels(): Partial<Record<LoggerDomain, LogLevel>> {
        const result: Partial<Record<LoggerDomain, LogLevel>> = {}
        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i)
                if (key?.startsWith(DOMAIN_PREFIX)) {
                    const domain = key.slice(DOMAIN_PREFIX.length) as LoggerDomain
                    const level = localStorage.getItem(key)
                    if (level && isValidLogLevel(level)) {
                        result[domain] = level
                    }
                }
            }
        } catch {
            // localStorage may not be available
        }
        return result
    }

    /**
     * Reset all logging configuration to defaults.
     */
    reset(): void {
        this.cachedGlobalLevel = null
        this.cachedDomainLevels.clear()
        try {
            const keysToRemove: string[] = [GLOBAL_KEY]
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i)
                if (key?.startsWith(DOMAIN_PREFIX)) {
                    keysToRemove.push(key)
                }
            }
            keysToRemove.forEach((key) => localStorage.removeItem(key))
        } catch {
            // localStorage may not be available
        }
        this.notify()
    }

    /**
     * Subscribe to configuration changes. The listener is invoked synchronously after every
     * mutation (set/clear/reset). Returns an unsubscribe function.
     */
    subscribe(listener: () => void): () => void {
        this.listeners.add(listener)
        return () => this.listeners.delete(listener)
    }

    /**
     * Apply a snapshot of configuration in a single batched mutation. Used by the worker
     * bridge to mirror main-thread config into a worker without persisting to storage.
     * Notifies subscribers once, after all values are applied.
     */
    applySnapshot(snapshot: {global: LogLevel; domains: Partial<Record<LoggerDomain, LogLevel>>}): void {
        this.cachedGlobalLevel = snapshot.global
        this.cachedDomainLevels.clear()
        for (const [domain, level] of Object.entries(snapshot.domains) as [LoggerDomain, LogLevel][]) {
            this.cachedDomainLevels.set(domain, level)
        }
        this.notify()
    }

    private notify(): void {
        this.listeners.forEach((listener) => listener())
    }
}

const VALID_LEVELS = new Set<LogLevel>(["error", "warn", "info", "log", "debug", "trace"])

function isValidLogLevel(value: string): value is LogLevel {
    return VALID_LEVELS.has(value as LogLevel)
}

/**
 * Singleton instance of LoggerConfig for convenience.
 */
export const loggerConfig = new LoggerConfig()
